import { AppSubjectEnum } from '../enums/app-subject.enum';
import { SubjectScopeEnum } from '../enums/subject-scope.enum';

/**
 * Exhaustive subject-to-scope map. Adding an {@link AppSubjectEnum} member
 * fails compilation until it is classified here.
 *
 * PLATFORM subjects are fleet-wide ops resources. TENANT covers operator
 * manage subjects and proto marketplace subjects (ACCOUNT, ASSET, …) that a
 * tenant wildcard must still be able to grant. SYSTEM, USER, FILE, and
 * AUDIT_LOG are tenant-scoped: they are used on tenant session principals and
 * must not be reachable via `manage:platform_all`.
 */
export const SUBJECT_SCOPE_BY_SUBJECT: Record<
	AppSubjectEnum,
	SubjectScopeEnum
> = {
	[AppSubjectEnum.ALL]: SubjectScopeEnum.WILDCARD,
	[AppSubjectEnum.PLATFORM_ALL]: SubjectScopeEnum.WILDCARD,

	[AppSubjectEnum.TENANT]: SubjectScopeEnum.PLATFORM,
	[AppSubjectEnum.TENANT_METADATA]: SubjectScopeEnum.PLATFORM,
	[AppSubjectEnum.INBOX]: SubjectScopeEnum.PLATFORM,
	[AppSubjectEnum.FILEBOX]: SubjectScopeEnum.PLATFORM,
	[AppSubjectEnum.OUTBOX]: SubjectScopeEnum.PLATFORM,
	[AppSubjectEnum.PLATFORM_SETTINGS]: SubjectScopeEnum.PLATFORM,
	[AppSubjectEnum.BILLABLE_EVENT]: SubjectScopeEnum.PLATFORM,
	[AppSubjectEnum.ADDON]: SubjectScopeEnum.PLATFORM,
	[AppSubjectEnum.STORAGE]: SubjectScopeEnum.PLATFORM,

	[AppSubjectEnum.SYSTEM]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.SETTINGS]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.USER_SETTINGS]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.USER_APP_SETTINGS]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.APPEARANCE]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.POLICY]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.USER]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.FILE]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.TAG]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.AUDIT_LOG]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.CHANGE_LOG]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.CONTACT]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.DRIVER]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.CUSTOMER]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.LOCATION]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.SUPPLIER]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.AGENT]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.AGENT_CATEGORY]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_FEATURE_ITEM]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_FEATURE_ITEM_FILE]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_GROUP]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_CATEGORY]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_CATEGORY_FEATURE_ITEM]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_CATEGORY_FEATURE_ITEM_FILE]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_CATEGORY_PRICING_TIER_ITEM]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_DAMAGE]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_GPS_TRACKING]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_INSURANCE]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_INSURANCE_CLAIM]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_MAINTENANCE]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_ROAD_TAX]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_ROAD_WORTHY_CHECK]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_COLOR]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_MAKE]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.VEHICLE_MODEL]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.RENTAL_AGREEMENT]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.RENTAL_AGREEMENT_PARTY]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.RENTAL_AGREEMENT_PARTY_DRIVING_LICENSE]:
		SubjectScopeEnum.TENANT,
	[AppSubjectEnum.RENTAL_RESERVATION]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.RENTAL_RESERVATION_PARTY]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.RENTAL_RESERVATION_PARTY_DRIVING_LICENSE]:
		SubjectScopeEnum.TENANT,
	[AppSubjectEnum.RENTAL_SNAPSHOT]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.AGREEMENT_CONTACT]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.TRANSACTION]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.PAYMENT]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.PAYMENT_ACTIVITY_TRACKING]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.FILE_LOG]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.NOTIFICATION_PREFERENCES]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.NOTIFICATION_LOG]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.REMINDER_PREFERENCES]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.REMINDER_LOG]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.PROTO_PREFERENCES]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.PROTO_LOG]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.REPORT]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.REPORT_TEMPLATE]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.REPORT_JOB]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.DATA_EXPORT_JOB]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.ACCOUNT]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.ASSET]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.ASSET_FEATURE_ITEM]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.ASSET_PRICING_ITEM]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.ASSET_RESERVATION]: SubjectScopeEnum.TENANT,
	[AppSubjectEnum.TENANT_SETTINGS]: SubjectScopeEnum.TENANT,
};

function subjectsOfScope(scope: SubjectScopeEnum): AppSubjectEnum[] {
	return (Object.values(AppSubjectEnum) as AppSubjectEnum[]).filter(
		(subject) => SUBJECT_SCOPE_BY_SUBJECT[subject] === scope,
	);
}

export const PLATFORM_SUBJECTS = subjectsOfScope(SubjectScopeEnum.PLATFORM);
export const TENANT_SUBJECTS = subjectsOfScope(SubjectScopeEnum.TENANT);
